import {replaceEmptyStringsOnNull} from "./ReplaceEmptyStringsOnNull";

/** Числа из полей type="number" приходят строкой; API ждёт number. */
const toApiNumber = (value) => {
    if (value === "" || value == null) {
        return null;
    }
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
    }
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
};

const snapshotScalar = (value) => (value === "" || value == null ? null : String(value));

const snapshotFieldChanged = (data, snapshot, key) => {
    if (!snapshot) {
        return true;
    }
    return snapshotScalar(data?.[key]) !== snapshotScalar(snapshot?.[key]);
};

export const takeModuleCompositionFormSnapshot = (data) => ({
    width: data?.width,
    height: data?.height,
    depth: data?.depth,
    name: data?.name,
    groupName: data?.groupName,
    sideBaseModule: data?.sideBaseModule,
    heightLocation: data?.heightLocation,
    coordinateXPoint: data?.coordinateXPoint ?? 0,
    coordinateYPoint: data?.coordinateYPoint ?? 0,
    priority: data?.priority ?? 0,
    minWidth: data?.minWidth,
    minHeight: data?.minHeight,
    minDepth: data?.minDepth,
    maxWidth: data?.maxWidth,
    maxHeight: data?.maxHeight,
    maxDepth: data?.maxDepth,
    patternW: data?.patternW,
    patternD: data?.patternD,
    patternH: data?.patternH,
    blockSize: Boolean(data?.blockSize),
});

export const moduleCompositionWidthChanged = (data, snapshot) =>
    snapshotFieldChanged(data, snapshot, "width");

const CATALOG_SNAPSHOT_KEYS = [
    "height",
    "depth",
    "name",
    "groupName",
    "sideBaseModule",
    "heightLocation",
    "minWidth",
    "minHeight",
    "minDepth",
    "maxWidth",
    "maxHeight",
    "maxDepth",
    "patternW",
    "patternD",
    "patternH",
];

export const moduleCompositionCatalogChanged = (data, snapshot) =>
    CATALOG_SNAPSHOT_KEYS.some((key) => snapshotFieldChanged(data, snapshot, key));

const PATCH_METADATA_KEYS = [
    "name",
    "sideBaseModule",
    "coordinateXPoint",
    "coordinateYPoint",
    "priority",
    "heightLocation",
];

export const moduleCompositionPatchMetadataChanged = (data, snapshot) =>
    PATCH_METADATA_KEYS.some((key) => snapshotFieldChanged(data, snapshot, key))
    || snapshotFieldChanged(data, snapshot, "blockSize");

export const moduleCompositionBlockSizeChanged = (data, snapshot) =>
    Boolean(data?.blockSize) !== Boolean(snapshot?.blockSize);

/** Габариты с бэка на вложенном `module`, не на строке composition (fallback — legacy). */
export const getModuleCompositionDimension = (row, key) => {
    const nested = row?.module && typeof row.module === "object" ? row.module : {};
    if (nested[key] !== undefined && nested[key] !== null && nested[key] !== "") {
        return nested[key];
    }
    return row?.[key];
};

const readModuleCompositionDimension = getModuleCompositionDimension;

/** PATCH /organizations/compositions/{compositionId}?moduleId= — flat body per OpenAPI. */
export const buildOrganizationCompositionModulePatchBody = (
    compositionModule,
    overrides = {},
) => {
    if (compositionModule == null || typeof compositionModule !== "object") {
        return null;
    }
    const nested = compositionModule.module && typeof compositionModule.module === "object"
        ? compositionModule.module
        : {};
    const pickRow = (key) =>
        overrides[key] !== undefined ? overrides[key] : compositionModule[key];
    const pickDim = (key) =>
        overrides[key] !== undefined ? overrides[key] : readModuleCompositionDimension(compositionModule, key);
    const pickNested = (key) =>
        overrides[key] !== undefined ? overrides[key] : nested[key] ?? compositionModule[key];

    return replaceEmptyStringsOnNull({
        id: pickRow("id"),
        name: pickRow("name") ?? nested.name,
        heightLocation: pickNested("heightLocation"),
        blockSize: Boolean(
            overrides.blockSize !== undefined
                ? overrides.blockSize
                : nested.blockSize ?? compositionModule.blockSize,
        ),
        width: toApiNumber(pickDim("width")),
        height: toApiNumber(pickDim("height")),
        depth: toApiNumber(pickDim("depth")),
        coordinateXPoint: toApiNumber(
            overrides.coordinateXPoint !== undefined
                ? overrides.coordinateXPoint
                : compositionModule.coordinateXPoint ?? 0,
        ),
        coordinateYPoint: toApiNumber(
            overrides.coordinateYPoint !== undefined
                ? overrides.coordinateYPoint
                : compositionModule.coordinateYPoint ?? 0,
        ),
        coordinateX3DPoint: toApiNumber(
            overrides.coordinateX3DPoint !== undefined
                ? overrides.coordinateX3DPoint
                : compositionModule.coordinateX3DPoint ?? 0,
        ),
        coordinateY3DPoint: toApiNumber(
            overrides.coordinateY3DPoint !== undefined
                ? overrides.coordinateY3DPoint
                : compositionModule.coordinateY3DPoint ?? 0,
        ),
        sideBaseModule: pickRow("sideBaseModule"),
        priority: toApiNumber(pickRow("priority") ?? 0),
    });
};

/** Body for adding a module to composition (same PATCH, catalog module picked in modal). */
export const buildOrganizationCompositionModulePatchBodyFromAddForm = (
    formState,
    catalogModule = {},
) => {
    if (formState == null || typeof formState !== "object") {
        return null;
    }
    return buildOrganizationCompositionModulePatchBody(
        {
            id: formState.id ?? 0,
            name: formState.name ?? catalogModule.name,
            sideBaseModule: formState.sideBaseModule,
            priority: formState.priority,
            coordinateXPoint: formState.coordinateXPoint,
            coordinateYPoint: formState.coordinateYPoint,
            coordinateX3DPoint: formState.coordinateX3DPoint,
            coordinateY3DPoint: formState.coordinateY3DPoint,
            width: formState.width ?? catalogModule.width,
            height: formState.height ?? catalogModule.height,
            depth: formState.depth ?? catalogModule.depth,
            blockSize: formState.blockSize ?? catalogModule.blockSize,
            module: {
                heightLocation: formState.heightLocation ?? catalogModule.heightLocation,
                blockSize: formState.blockSize ?? catalogModule.blockSize,
            },
        },
        {},
    );
};

/** PATCH module-compositions/{id}/width — тело по OpenAPI (id в path = module-composition). */
export const buildModuleCompositionWidthPatchPayload = (data, moduleCompositionId) => {
    if (data == null || moduleCompositionId == null || moduleCompositionId === "") {
        return null;
    }
    const nested = data.module && typeof data.module === "object" ? data.module : {};
    const moduleId = data.id ?? nested.id;
    if (moduleId == null || moduleId === "") {
        return null;
    }
    return replaceEmptyStringsOnNull({
        id: moduleCompositionId,
        name: data.name,
        coordinateXPoint: data.coordinateXPoint ?? 0,
        coordinateYPoint: data.coordinateYPoint ?? 0,
        sideBaseModule: data.sideBaseModule,
        priority: data.priority ?? 0,
        module: replaceEmptyStringsOnNull({
            id: moduleId,
            name: data.name,
            heightLocation: data.heightLocation,
            blockSize: Boolean(data.blockSize ?? nested.blockSize),
            width: toApiNumber(data.width),
            height: toApiNumber(data.height),
            depth: toApiNumber(data.depth),
        }),
    });
};

/** PATCH module-compositions/{id} — без width/height/depth (ширина — /width). */
export const buildModuleCompositionPatchPayload = (data, moduleCompositionId) => {
    if (data == null || moduleCompositionId == null || moduleCompositionId === "") {
        return null;
    }
    const nested = data.module && typeof data.module === "object" ? data.module : {};
    const moduleId = data.id ?? nested.id;
    if (moduleId == null || moduleId === "") {
        return null;
    }
    return replaceEmptyStringsOnNull({
        id: moduleCompositionId,
        name: data.name,
        coordinateXPoint: data.coordinateXPoint ?? 0,
        coordinateYPoint: data.coordinateYPoint ?? 0,
        sideBaseModule: data.sideBaseModule,
        priority: data.priority ?? 0,
        module: replaceEmptyStringsOnNull({
            id: moduleId,
            name: data.name,
            heightLocation: data.heightLocation,
            blockSize: Boolean(data.blockSize ?? nested.blockSize),
        }),
    });
};

/** PUT /modules — каталог без width (ширина — PATCH …/module-compositions/{id}/width). */
export const buildModuleCompositionCatalogPutPayload = (data) => replaceEmptyStringsOnNull({
    name: data.name,
    groupName: data.groupName,
    sideBaseModule: data.sideBaseModule,
    heightLocation: data.heightLocation,
    height: data.height,
    depth: data.depth,
    minWidth: data.minWidth,
    minHeight: data.minHeight,
    minDepth: data.minDepth,
    maxWidth: data.maxWidth,
    maxHeight: data.maxHeight,
    maxDepth: data.maxDepth,
    patternW: data.patternW,
    patternD: data.patternD,
    patternH: data.patternH,
});

export const mapModuleCompositionToModuleItem = (payload) => {
    if (payload == null || typeof payload !== "object") {
        return payload
    }
    const nested = payload.module && typeof payload.module === "object" ? payload.module : {}
    const {
        detailPatterns: nestedPatterns,
        variablesMap: nestedVars,
        relateDetails: nestedRelate,
        ...nestedRest
    } = nested
    const {
        detailPatterns,
        variablesMap,
        relateDetails,
        module,
        ...compositionRest
    } = payload

    return {
        ...nestedRest,
        ...compositionRest,
        width: nested.width ?? compositionRest.width,
        height: nested.height ?? compositionRest.height,
        depth: nested.depth ?? compositionRest.depth,
        minWidth: nested.minWidth ?? compositionRest.minWidth,
        minHeight: nested.minHeight ?? compositionRest.minHeight,
        minDepth: nested.minDepth ?? compositionRest.minDepth,
        maxWidth: nested.maxWidth ?? compositionRest.maxWidth,
        maxHeight: nested.maxHeight ?? compositionRest.maxHeight,
        maxDepth: nested.maxDepth ?? compositionRest.maxDepth,
        heightLocation: nested.heightLocation ?? compositionRest.heightLocation,
        blockSize: Boolean(nested.blockSize ?? compositionRest.blockSize),
        groupName: nested.groupName ?? compositionRest.groupName,
        patternW: nested.patternW ?? compositionRest.patternW,
        patternD: nested.patternD ?? compositionRest.patternD,
        patternH: nested.patternH ?? compositionRest.patternH,
        name: compositionRest.name ?? nested.name,
        id: nested.id ?? payload.moduleId ?? nestedRest.id,
        moduleCompositionId: payload.id,
        module: nested,
        detailPatterns: nestedPatterns ?? detailPatterns,
        variablesMap: nestedVars ?? variablesMap,
        relateDetails: nestedRelate ?? relateDetails,
        pacedModule: compositionRest.pacedModule ?? nested.pacedModule ?? null,
        dependModules: nested.dependModules ?? compositionRest.dependModules,
        fittings: nested.fittings ?? compositionRest.fittings,
    }
}
